import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { ProductCard, ProductTile } from '@notino/react-styleguide';

import { useFormatPrice } from '@components/PriceLabel/useFormattedPrice';
import { IConditionalVoucherDiscount } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount/ConditionalVoucherDiscount';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { styled } from '@linaria/react';

type Props = {
  voucherDiscount: IConditionalVoucherDiscount;
  product: { brand: { name: string } };
};

const Description = styled.div`
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.25rem;
  margin-top: 0.625rem;
`;

export const ProductTileConditionalDiscount = ({
  voucherDiscount: { discountConditions, targets, conditionType, tags },
  product: { brand },
}: Props) => {
  const formatPrice = useFormatPrice();

  const { release_discovery_product_card_2026 } = useFeatureFlags();

  const { percentageDiscount, conditionMin } = discountConditions.reduce(
    (prev, current) =>
      prev.discountAmount > current.discountAmount ? prev : current,
    { discountAmount: 0 } as (typeof discountConditions)[number]
  );

  function getVoucherDiscountDecoration() {
    if (tags?.includes('summerblackfriday')) {
      return 'summerBlackFriday';
    }
    if (tags?.includes('blackfriday')) {
      return 'blackFriday';
    }
  }

  const getDescription = () => {
    const perentageElement = <strong>- {percentageDiscount} %</strong>;
    const isBrandTargeted = targets[0]?.type === 'Brand';

    if (isBrandTargeted) {
      if (conditionType === 'Piece') {
        return (
          <FormattedMessage
            id="productCard.discount.conditionalDiscountBrandPieces"
            values={{
              label: perentageElement,
              brand: brand.name,
              count: conditionMin,
            }}
          />
        );
      }
      if (conditionType === 'Price') {
        return (
          <FormattedMessage
            id="productCard.discount.conditionalDiscountBrandPrice"
            values={{
              label: perentageElement,
              brand: brand.name,
              price: formatPrice({ value: conditionMin })!
                .formattedPriceWithCurrency,
            }}
          />
        );
      }
    }

    if (conditionType === 'Piece') {
      return (
        <FormattedMessage
          id="productCard.discount.conditionalDiscountCategoryPieces"
          values={{
            label: perentageElement,
            count: conditionMin,
          }}
        />
      );
    }
    if (conditionType === 'Price') {
      return (
        <FormattedMessage
          id="productCard.discount.conditionalDiscountCategoryPrice"
          values={{
            label: perentageElement,
            price: formatPrice({ value: conditionMin })!
              .formattedPriceWithCurrency,
          }}
        />
      );
    }
  };

  const DiscountComponent = release_discovery_product_card_2026
    ? ProductCard.Discount
    : ProductTile.Discount;

  if (!DiscountComponent) {
    return null;
  }

  const descriptionContent = getDescription();

  if (!descriptionContent) {
    return null;
  }

  const discountDescription = release_discovery_product_card_2026 ? (
    descriptionContent
  ) : (
    <Description>{descriptionContent}</Description>
  );

  return (
    <DiscountComponent
      decoration={getVoucherDiscountDecoration()}
      discountDescription={discountDescription}
    />
  );
};
